import { useLicensingServiceV1 } from "@rw1/api-services";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAllExperiences } from "../../../hooks/useAllExperiences";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import { ALL_LICENSES_QUERY_KEY } from "./useExternalLicenses";

export const LICENSE_BY_ID_QUERY_KEY = "license-by-id";

export interface UseLicenseByIdProps {
  licenseId?: string;
  enabled?: boolean;
}

export interface LicenseWithExperienceName {
  experienceName: string;
  licenseId: string;
  assignedOrganizationId: string;
  allocatedSeats: number;
  releaseChannelId: string;
  licenseName: string;
  issuingOrganizationId: string;
  seats?: number;
  expiresAt?: Date;
  renewalDate: Date;
  renewalPeriodDays: number;
  assignedOrganizationName: string;
  issuingOrganizationName: string;
  hasWhitelist: boolean;
}

export interface UseLicenseByIdResult {
  license: LicenseWithExperienceName | undefined;
  isLoading: boolean;
}

const mapLicenseWithExperienceName = (
  license: Licensing_V1.License,
  experienceName: string,
  assignedOrganizationName: string,
  issuingOrganizationName: string,
): LicenseWithExperienceName => {
  return {
    experienceName,
    licenseId: license.licenseId,
    assignedOrganizationId: license.assignedOrganizationId,
    allocatedSeats: license.allocatedSeats,
    releaseChannelId: license.releaseChannelId,
    licenseName: license.licenseName,
    issuingOrganizationId: license.issuingOrganizationId,
    seats: license.seats,
    expiresAt: license.expiresAt,
    renewalDate: license.renewalDate,
    renewalPeriodDays: license.renewalPeriodDays,
    assignedOrganizationName,
    issuingOrganizationName,
    hasWhitelist: license.hasWhitelist,
  };
};

export const useLicenseById = ({
  licenseId,
  enabled,
}: UseLicenseByIdProps): UseLicenseByIdResult => {
  const licensingService = useLicensingServiceV1();
  const queryClient = useQueryClient();

  const { experiencesMap, isLoading: isExperiencesLoading } =
    useAllExperiences();
  const { companyNamesByIdMap } = useCompaniesData();

  const { data, isLoading: isLicenseLoading } = useQuery({
    queryKey: [LICENSE_BY_ID_QUERY_KEY, licenseId],
    queryFn: async () => {
      if (!licenseId) return undefined;

      return await licensingService.getLicenseById(licenseId);
    },
    enabled: !!enabled && !!licenseId,
    staleTime: 5 * 60 * 1000, // 5 minutes

    initialData: () => {
      if (!licenseId) return undefined;

      // Get all queries that might contain the full list of licenses
      const queries = queryClient.getQueriesData({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });

      for (const [, queryData] of queries) {
        if (Array.isArray(queryData)) {
          // Find the specific license in the cached list
          const found = (queryData as Licensing_V1.License[]).find(
            (l) => l.licenseId === licenseId,
          );
          if (found) return found;
        }
      }

      return undefined;
    },
  });

  const license = useMemo<LicenseWithExperienceName | undefined>(() => {
    if (!data) return undefined;

    const experienceName =
      experiencesMap.get(data.releaseChannelId)?.friendlyName ?? "—";
    const assignedOrganizationName =
      companyNamesByIdMap[data.assignedOrganizationId] ?? "—";
    const issuingOrganizationName =
      companyNamesByIdMap[data.issuingOrganizationId] ?? "—";

    return mapLicenseWithExperienceName(
      data,
      experienceName,
      assignedOrganizationName,
      issuingOrganizationName,
    );
  }, [data, experiencesMap, companyNamesByIdMap]);

  return {
    license,
    isLoading: isExperiencesLoading || isLicenseLoading,
  };
};
